/**
 * ================================================================================================
 *  WHAT THIS ESTATE IS CONNECTED TO, IN THE OPERATOR'S LANGUAGE — ONE COPY, TWO SCREENS.
 * ================================================================================================
 *
 * **Owner section 8, 2026-11-01:** *"כרטיס הדואר מציג ״נדרשת הגדרה״ אף שכבר בוצעה שליחה מוצלחת
 * מתוך המוצר. אמת שהכרטיס משקף את מצב החיבור בפועל"*, and about the other tile beside it:
 * *"קארדקום עדיין מוצג כ״מתוכנן — לא זמין״. מסלול מדומה אינו החיבור שביקשתי לראות."*
 *
 * ── WHY THESE MAPS LEFT THE HEALTH SCREEN ─────────────────────────────────────────────────────
 *
 * The platform CONSOLE draws three "connections" tiles, and all three stated their condition in a
 * STRING LITERAL. The mail tile said *"נדרשת הגדרה"* on every estate — including one whose SMTP
 * submission had just delivered a message, which is the Owner's finding — and the clearing tile
 * said *"מתוכנן — עדיין לא זמין"* whether the estate had no provider, the sandbox, or a terminal.
 * The product knew all of it: `platform.health.read` has carried `mailChannel` since X8 and
 * carries `paymentProvider` from today.
 *
 * So the console reads the same answer the health screen reads, through the SAME map. A second
 * copy of these sentences is the thing this department keeps finding drifted — two wordings of one
 * rule diverge long before anyone notices one of them is wrong.
 *
 * `mail-channel-labels` (guardrails) follows the map here. Its claim is unchanged and now covers
 * both consumers: every census name the composition can produce has an explanation, and no
 * explanation exists for a name nothing produces.
 */

import type { Tone } from './adminVocabulary';

/**
 * THE OUTGOING-MAIL PATH, PER CENSUS STATE — X7 item 1.
 *
 * `mailChannel` arrives as the server's own census name for the `MailPort` it composed. It has
 * exactly one producer, `composedMailChannel()` (`apps/api/src/composition/production.ts:325-335`),
 * which filters the construction census for port names beginning `MailPort` and THROWS on 0 and on 2
 * — so a live estate can report exactly two strings, both registered at the site of their `new`
 * (`apps/api/src/composition/services.ts:568, 577-580`). A blank key throws, an unusable directory
 * throws, and all of those unwind into boot, so this screen never renders them.
 *
 * ── NEITHER VALUE MAY BE READABLE AS "MAIL WORKS", AND THAT IS THE WHOLE ROW ────────────────────
 *
 * One arm delivers nothing at all and is what production gets; the other writes the live invitation
 * or reset code to a file in clear text. A green chip on either would be the fabricated healthy state
 * this screen exists to refuse, so there is no `ok` tone in this map and the label carries no verdict:
 * the siblings label the SUBJECT and let the value speak (`אירועים שנרשמו`, `מתוכם סירובים`), and
 * `דואר יוצא — תקין` would have been the fabrication.
 *
 * The file-sink parenthetical is bounded by what the product actually does: a resend MINTS A NEW token
 * (`api/contract.ts:999`). It does not claim the previous token is invalidated, because nothing at
 * this head states that.
 */
export const MAIL_CHANNEL_HE: Readonly<
  Record<string, { readonly valueHe: string; readonly bodyHe: string; readonly tone: Tone }>
> = {
  // THE SENDING PATH. `ok`, and the body says what "ok" does and does not cover: the server took
  // the message. Whether it reached a mailbox depends on SPF, DKIM, DMARC and the recipient's own
  // filters, none of which this product can see — and a screen that read "mail works" on the
  // strength of a successful handover would be reporting the one half it can measure.
  'MailPort (smtp submission — MESSAGES LEAVE THE HOST)': {
    valueHe: 'שליחה בדואר — ההודעות נמסרות לספק',
    bodyHe:
      'בנתיב הזה הזמנות והודעות איפוס סיסמה נמסרות לשרת הדואר של הספק בחיבור מוצפן ומאומת, ויוצאות מהשרת. מה שמוצג כאן הוא שהספק קיבל את ההודעה — זו אינה אישור שההודעה הגיעה לתיבת הדואר: הגעה תלויה גם ברשומות SPF, DKIM ו-DMARC של הדומיין ובסינון אצל הנמען, ואין למערכת דרך לראות אותם. אם נמען מדווח שלא קיבל — יש לבדוק ביומן הביקורת שההודעה נמסרה, ואת הגדרות הדומיין אצל ספק הדואר.',
    tone: 'ok',
  },
  'MailPort (dev sink — RECORDS, DELIVERS NOTHING)': {
    valueHe: 'אין שליחה — ההודעות נרשמות ואינן נשלחות',
    bodyHe:
      'בנתיב הזה הזמנות והודעות איפוס סיסמה נרשמות במערכת ואינן מגיעות לאף תיבת דואר. אין בגרסה הזו שליחת דואר, ולכן אי אפשר להשלים הזמנה או איפוס סיסמה דרך הדואר בלבד.',
    // `warn`, not `ok` and not `error`: it is the composed and INTENDED state of this build, and it is
    // still not a working mail path.
    tone: 'warn',
  },
  'MailPort (file sink — STAGING ONLY, WRITES A CREDENTIAL TO DISK)': {
    valueHe: 'כתיבה לקבצים בלבד — מיועד לסביבת בדיקות',
    bodyHe:
      'בנתיב הזה ההודעות נכתבות כקבצים בתיקייה על השרת ואינן נשלחות לאף תיבת דואר. כל קובץ מכיל את קוד ההזמנה או קוד איפוס הסיסמה עצמו בטקסט גלוי, וכל מי שיש לו גישה לתיקייה יכול להשתמש בו. הנתיב הזה מיועד לסביבת בדיקות; אם זו סביבת ייצור — יש להסיר את ההגדרה הזו ולהנפיק מחדש כל הזמנה שנכתבה לתיקייה (שליחה מחדש יוצרת קוד חדש).',
    // `error`. `FileMailSink` turns a directory into an unaudited credential store, and that is the
    // one fact on this screen an operator must not have to infer.
    tone: 'error',
  },
};

/**
 * THE UNRECOGNISED-NAME ARM — a mapping fallback, not a third product state.
 *
 * The wire type is `string`, not a union (`platform-health.ts:169`, `api/contract.ts:650`), so the
 * client has an unavoidable unknown arm. This is the same idiom the screen already uses twice for a
 * code it does not know (`?? 'תקלה ביומן הביקורת.'`, `?? 'תחום שאינו נמדד בגרסה הזו.'`): it invents no
 * state, it refuses to present an unknown one as working.
 */
export const MAIL_CHANNEL_UNKNOWN_HE = {
  valueHe: 'הנתיב שדווח אינו מזוהה במסך הזה',
  bodyHe:
    'השרת דיווח על נתיב דואר שאין לו תיאור כאן. אין לקרוא את השורה הזו כאישור שדואר נשלח. השם שדיווח השרת מוצג כפי שהוא.',
  tone: 'warn',
} as const;

/**
 * WHICH PAYMENT PROVIDER THIS ESTATE COMPOSED — the clearing tile's three real states.
 *
 * The names are the PORT'S OWN `provider` values, from
 * `apps/api/src/composition/payment-methods.ts`: `none` is the refusing adapter, `sandbox` is the
 * Staging checker that asks nobody, and anything else is a provider that was actually contacted.
 *
 * NONE OF THE THREE IS A GREEN CHIP SAYING "CLEARING WORKS", and two of them are explicit that no
 * money can move. The Owner's rule for the whole round applies here as hard as anywhere:
 * *"אין להציג יכולת מדומה כפעילה."*
 */
export const PAYMENT_PROVIDER_HE: Readonly<
  Record<string, { readonly valueHe: string; readonly bodyHe: string; readonly tone: Tone }>
> = {
  none: {
    valueHe: 'אין ספק סליקה מוגדר — הרשמה עצמית אינה יכולה להסתיים',
    bodyHe:
      'באסטייט הזה לא מוגדר ספק סליקה, ולכן שלב בדיקת אמצעי התשלום בהרשמה עצמית מסרב בשם — ההרשמה נעצרת לפניו ולא נוצר ארגון. כדי להפעיל סליקה נדרשים שלושה ערכים של קארדקום: TerminalNumber, ApiName ו-ApiPassword. הסיסמה נדרשת לשתי פעולות שבלעדיהן שתי דרישות אינן ניתנות למימוש — קישור למסמך המקורי, וזיכוי או ביטול של חיוב. שלושתם אישורי גישה: הם נכנסים למחסן הסודות של השרת, לא לקובץ הגדרות ולא להודעה. בנוסף נדרשת כתובת HTTPS ציבורית שאליה חברת הסליקה מחזירה את הדפדפן ושולחת דיווח. לאחר שהארבעה קיימים, המוצר פותח ללקוח עמוד תשלום של קארדקום ומאמת את התוצאה בצד השרת. אין חיוב בשלב הזה, ולא משום שאין סכום — הסכום נדרש על ידי קארדקום גם לבדיקת כרטיס — אלא משום שהפעולה היא CreateTokenOnly עם JValidateType=2, כלומר אימות כרטיס ללא עיכוב מסגרת. חיוב מחזורי, אצוות, ניסיונות חוזרים ותקופת חסד — טרם נבנו.',
    tone: 'warn',
  },
  sandbox: {
    valueHe: 'מסלול בדיקה — אף ספק אינו נשאל, ואין חיוב',
    bodyHe:
      'באסטייט הזה שלב אמצעי התשלום עובר בלי לשאול אף ספק, כדי שאפשר יהיה לעבור מסלול הרשמה שלם בבדיקה — *"אין לבצע חיובים אמיתיים בבדיקה"*. כל שורה נרשמת כ-sandbox, כך שאי אפשר לטעות ולקרוא אותה כאישור של ספק. זה אינו חיבור לקארדקום: אין טרמינל, אין כרטיס, ולא עברה אגורה.',
    tone: 'warn',
  },
  cardcom: {
    valueHe: 'קארדקום — אמצעי תשלום נבדק מול הספק',
    bodyHe:
      'הלקוח מזין את הכרטיס בעמוד של קארדקום, והתוצאה נקראת מהם בצד השרת ולא מהדפדפן. בדיקה בלבד: הפעולה היא CreateTokenOnly עם JValidateType=2 — אימות כרטיס בלי חיוב ובלי עיכוב מסגרת. הסכום שנשלח הוא הסכום שהוצג ללקוח, מתוך המחירון שפורסם; קארדקום דורשת סכום גם לבדיקת כרטיס, ולכן הוא נשלח ואינו מומצא. פרטי הכרטיס המלאים ו-CVV אינם עוברים דרך המערכת הזאת בשום שלב — מה שנשמר הוא אסימון שמצביע על אמצעי תשלום שקארדקום מחזיקה. חיוב מחזורי, אצוות, ניסיונות חוזרים ותקופת חסד — טרם נבנו.',
    tone: 'ok',
  },
};

/** The unrecognised-name arm, exactly as the mail map has one and for the same reason. */
export const PAYMENT_PROVIDER_UNKNOWN_HE = {
  valueHe: 'הספק שדווח אינו מזוהה במסך הזה',
  bodyHe:
    'השרת דיווח על ספק סליקה שאין לו תיאור כאן. אין לקרוא את השורה הזו כאישור שסליקה פעילה. השם שדיווח השרת מוצג כפי שהוא.',
  tone: 'warn',
} as const;
